// Getting one activation's data out in a form a client can use.
//
// Minimal on purpose. This is the file that hands personal data to somebody
// outside the building, so the thing to optimise is not how many columns it can
// carry, it is how obvious it is what is in it and why. The rule it applies is
// in exportRule.ts with the argument for each of its three cases, and it is
// carried inside every file this produces rather than left in the repository.
//
// Two structural decisions worth naming.
//
// EVERY QUERY READS spine_live, which is the rule for every read surface, and
// it is what makes case 1 of the export rule true without a line of code: a
// voided capture is absent because the view resolved the void chain, not
// because something here remembered to filter. The one query that reads
// spine_arrival is the one counting what was excluded, and it exists so the
// manifest can explain the difference rather than leave it as a discrepancy.
//
// format_context IS NOT READ HERE AT ALL, and that is deliberate rather than an
// omission. Constraint 2 permits a report to display it, and mechanism 2 is
// structural withholding: a value the reporting layer never receives is a value
// no future where clause can reach. Phase 1 has no format loops, so carrying it
// now would be carrying a display field nothing displays, in the one file where
// a stray filter would quietly turn a format-agnostic export into a per-format
// one. When the format loops exist and there is something to show, it comes
// back as a display-only pass-through, which is a smaller change than unpicking
// a filter written against it in the meantime.
//
// ---------------------------------------------------------------------------
// AND A THIRD, ADDED 2026-08-28: AN EXPORT NOW HAS A PURPOSE, AND THE PURPOSE
// DECIDES BOTH WHO IS IN THE FILE AND WHICH COLUMNS IT CARRIES.
//
// exportPurpose.ts holds that argument in full and it is the important half of
// this change. Two consequences land in this file and neither is obvious.
//
// THE COLUMN LIST NOW REACHES THE SQL, so the query selects the contact slots
// this file will actually carry and no others. Discarding a column after
// selecting it would have been three lines shorter and would have meant the
// service process holds every attendee's telephone number in order to throw it
// away — and a value that was never selected is a value no later mistake in
// this file can put on the wire. It is the same move the header makes about
// format_context, applied to the columns a caller did not ask for.
//
// Nothing a caller sends becomes SQL. COLUMN_SQL below is a static map from the
// frozen column list to a fixed expression, and a name absent from it
// contributes nothing; resolveShape has already refused any name that is not in
// the frozen list. The column list is not user input by the time it arrives
// here, and it is treated as though it were.
//
// THE TWO KINDS OF ABSENCE ARE NOT THE SAME AND THE FILE HAS TO SAY WHICH.
// A column that is present and null was withheld by consent, and the row names
// it in `withheld_fields` and explains it in a sentence. A column that is
// missing from the row entirely was not asked for, and the manifest names the
// full set the template could have carried. Collapsing those would make a
// minimised export indistinguishable from an activation where nobody consented
// to anything, which is the most misleading file this system could produce: it
// would read as a consent failure and be a caller preference.

import { toSafeInteger, utcIso, type Queryable } from "../../api/src/db.js";
import { describeAccess, requireAccess, type AccessPosition } from "./access.js";
import {
  discloseToClient,
  EXPORT_RULE,
  type ConsentPurpose,
  type ConsentState,
  type ConsentStates,
  type DisclosureReason,
} from "./exportRule.js";
import { readFreeTextQuestions } from "./survey.js";
import { readSpins, type WheelSpinLine } from "./wheelResults.js";
import {
  COLUMN_DEFINITIONS,
  ExportAgreementMissing,
  assertPurposeHonoured,
  purposeFor,
  resolveShape,
  rowSatisfies,
  type ExportColumn,
  type ExportPurposeId,
  type ResolvedShape,
} from "./exportPurpose.js";

/**
 * The account of itself every row carries, whatever the purpose and whatever
 * the caller asked for.
 *
 * Separated from the optional half so that the type says which is which.
 * UNMINIMISABLE_COLUMNS in exportPurpose.ts is the same list and the runtime
 * gate checks against it; this is the compile-time half of the same statement.
 */
export type ExportRowConsentAccount = {
  attendee_ref: string;
  consent: ConsentStates;
  /** Why the fields above look the way they do, in one word and then in a sentence. */
  personal_data: DisclosureReason;
  /**
   * Fields this file would have carried and consent removed.
   *
   * Only ever names columns this purpose and this field list include. A column
   * the caller did not ask for is not "withheld" — nothing withheld it — and
   * listing it here would tell a client their attendees declined something
   * nobody asked them about.
   */
  withheld_fields: string[];
  personal_data_note: string;
};

/**
 * One person's row. The consent account is always here; everything else depends
 * on the purpose and on what the caller asked for.
 */
export type ExportRow = ExportRowConsentAccount &
  Partial<{
    registered_at: string;
    format: string | null;
    display_name: string | null;
    contact_email: string | null;
    deliver_to_email: string | null;
    phone: string | null;
    visits: number;
    /** survey_free_text only: each free-text question this person answered, and what they wrote. */
    free_text: ExportFreeText[];
    /** wheel_results only: this person's spin. */
    wheel: ExportWheelSpin;
  }>;

/** One person's spin of the prize wheel, as a wheel_results file carries it (156). */
export type ExportWheelSpin = {
  spun_at: string;
  lane: "onsite" | "web" | null;
  landed_on: string;
  prize: boolean;
  claim_code: string | null;
  claim_expires_at: string | null;
  collected_at: string | null;
};

/** One written answer, with the question it answers. */
export type ExportFreeText = {
  field_id: string;
  question: string;
  answer: string;
};

/**
 * A withdrawal, exported whether or not the person has a row above.
 *
 * Carries no contact details, on the same reasoning as the directory channel in
 * the sync contract: this is an instruction to act on a record the client
 * already holds, and it needs a key and a purpose rather than a person.
 *
 * NOT SUBJECT TO THE PURPOSE'S CONSENT REQUIREMENT, and that is deliberate to
 * the point of being the reason this section exists. A marketing list drops
 * everybody who declined; if it also dropped the withdrawal records it would be
 * a file that quietly stopped telling the client to stop contacting the person
 * who had just asked them to. Case 2 of the export rule is stronger than any
 * purpose: the withdrawal is the one thing that must never be filtered out.
 */
export type ExportWithdrawal = {
  attendee_ref: string | null;
  consent_version_id: string | null;
  purpose: string | null;
  method: string | null;
  withdrawn_at: string;
};

/** The exact words a person was shown, for every consent cited in this file. */
export type ExportWording = {
  wording_hash: string;
  consent_version_id: string;
  purpose: string;
  locale: string;
  body: string;
};

/** How many people this purpose left out, and on which requirement. */
export type ExportOmission = {
  /** Counted registrations that are not in this file. Zero for an ungated purpose. */
  total: number;
  /**
   * Per requirement, how many of the omitted lacked it.
   *
   * NOT MUTUALLY EXCLUSIVE and the note says so. Somebody who consented to
   * nothing is counted against all three requirements, so these figures sum to
   * more than `total` and a reader who adds them up must be told that before
   * they do it rather than after.
   */
  by_requirement: {
    purpose: ConsentPurpose;
    /** Agreed once and took it back. */
    withdrawn: number;
    /** No live consent and no withdrawal, so either they declined or nobody asked. */
    never: number;
  }[];
  note: string;
};

export type ExportPurposeManifest = {
  id: ExportPurposeId;
  template_id: string;
  says: string;
  responsibility: string;
  /** Consent purposes a person must hold live to be in this file at all. */
  requires: ConsentPurpose[];
  /** The columns this file carries. */
  columns: ExportColumn[];
  /** The columns this template could have carried, so a caller can see what they did not ask for. */
  columns_available: ExportColumn[];
  minimised: boolean;
  omitted: ExportOmission;
  /**
   * Required purposes for which this activation holds no consent event at all.
   *
   * optIn.ts's finding, applied where it bites hardest. There is no event for a
   * refusal, so a purpose with no event is ambiguous between "nobody was asked"
   * and "everybody refused" — opposite facts, and one of them means this file is
   * empty for a reason that has nothing to do with consent.
   */
  unobserved_requirements: {
    purposes: ConsentPurpose[];
    note: string;
  };
  /** The purpose's own rules, in sentences, carried in the file. */
  rule: string[];
};

/**
 * When this data goes, said rather than assumed.
 *
 * THE HONEST ANSWER IS THAT IT DOES NOT, and putting that in the file is the
 * whole point. xp/RETENTION.md opens by stating that nothing in XP1 deletes
 * attendee data and that the honest answer to "how long is it kept" is forever.
 * A manifest that omitted retention would let a client assume there is a period
 * somewhere; a manifest that invented one would be worse, because a retention
 * period a system cannot execute is, in that document's words, a promise with
 * nothing behind it.
 *
 * `period` is therefore null on both halves, and null is a value rather than an
 * absence: a screen rendering this shows "not set" where it would otherwise show
 * nothing at all, and "not set" is a sentence somebody argues with. The
 * decisions that would fill it in are section 6 of RETENTION.md and belong to a
 * person, not to this code — writing a number here would be answering the
 * question the legal reviewer is being asked.
 */
export type ExportRetention = {
  /** What the system this file came from does with the data afterwards. */
  server: {
    /** Whether anything deletes attendee data on a schedule. No retention period exists, so no. */
    deletes_attendee_data: boolean;
    /** Whether one person's details can be erased on request (153): an Admin's Erase. */
    erases_on_request: boolean;
    /** Months. Null means no period is set, which is not the same as unknown. */
    period_months: number | null;
    says: string;
  };
  /** What happens to this file, which is a different question with a different owner. */
  this_file: {
    period_months: number | null;
    held_by: string;
    says: string;
  };
  /** Where the argument lives, so the sentence above can be checked rather than believed. */
  source: string;
};

export type ExportManifest = {
  activation_id: string;
  generated_at: string;
  actor_id: string;
  actor_role: "nova_admin" | "nova_editor" | "client_viewer";
  access: AccessPosition;
  access_note: string;
  row_count: number;
  withdrawal_count: number;
  /**
   * The highest spine_seq visible for this activation when this ran.
   *
   * 03 section 5.8a rules that an export issued at close can change afterwards,
   * because a device offline across the close still syncs days later, and that
   * sealing the report at issue was considered and rejected. This number is
   * what makes the change detectable: two exports of the same activation with
   * different high-water marks are two different days of knowledge, and the
   * later one is the true one.
   */
  spine_high_water: number;
  excluded_voided: {
    registrations: number;
    all_events: number;
    note: string;
  };
  /**
   * Rows in this file that carry less than they could, because of consent.
   *
   * Distinct from `purpose.omitted`, which counts people who are not here at
   * all. Both numbers are needed and neither implies the other: a participation
   * report withholds and omits nobody, a marketing list omits and withholds
   * nobody, and reading either count as the other would misdescribe the file.
   */
  withheld: {
    withdrawn: number;
    never_granted: number;
  };
  /**
   * What an Admin's actions on people's records did to this file (152).
   *
   * Withheld people are absent from every file. An erased person is a row with
   * nothing identifying on it in a file that has a row for everybody, and is
   * absent from one that requires a consent, since their consents no longer
   * speak for a person anybody may contact. Counted so the file still
   * reconciles against the participation report and the operator view.
   */
  admin_actions: {
    withheld_and_absent: number;
    erased: number;
    erased_and_absent: number;
    note: string;
  };
  purpose: ExportPurposeManifest;
  retention: ExportRetention;
  rule: readonly string[];
  caveats: string[];
  /**
   * What the columns mean, for the columns whose values are a closed list.
   *
   * Built from the columns actually in this file rather than from the whole
   * library, so a definition for a column the reader cannot see never appears —
   * that is how a definitions block turns into boilerplate people stop reading.
   */
  column_definitions: Record<string, string>;
};

export type ActivationExport = {
  manifest: ExportManifest;
  rows: ExportRow[];
  withdrawals: ExportWithdrawal[];
  consent_wording: ExportWording[];
  /**
   * The `export_generated` payload this run is recorded as.
   *
   * IT IS WRITTEN NOW. This comment used to say it was "handed back for whoever
   * owns that mechanism", and nobody owned that mechanism, so every export of
   * attendee personal data this system has produced persisted no record
   * anywhere. Migration 011 is the mechanism: `report_access` holds it in the
   * `detail` column of the row the read is already being recorded under, and
   * xp/api/src/reports/routes.ts files it as the read completes.
   *
   * STILL RETURNED RATHER THAN WRITTEN *HERE*, and the reason has changed from
   * a gap to a constraint. The `Queryable` this function is handed is a
   * transaction that reports/routes.ts rolls back on purpose, so an insert
   * issued on it would be discarded without an error — and for a client viewer
   * that transaction has dropped to xp_reader, which 011 grants nothing on
   * report_access. Writing it here is therefore not merely inconvenient, it is
   * two separate kinds of wrong. It goes back to the route, which has a
   * connection of its own.
   *
   * The original gap is still real and is still the reason this is not a spine
   * event: the spine envelope requires a device_id, a client_sequence and a
   * device_signature, an export is not a device, and 011's header argues at
   * length that minting a synthetic one would cost the spine the single
   * property that makes it worth having.
   *
   * It is also still validated against the frozen payload shape by check.mjs,
   * which now matters more rather than less: this shape is what goes into a
   * jsonb column, and a payload that drifts is an audit trail whose older rows
   * cannot be read alongside its newer ones.
   *
   * THE PURPOSE TRAVELS IN IT, and it travels inside the frozen shape rather
   * than beside it. `export_template_id` differs per purpose and
   * `scope.filters_applied` names the purpose and every consent it required, so
   * `report_access.detail` can distinguish somebody taking a participation
   * report from somebody taking a mailing list. Those are very different acts,
   * and a table that recorded them identically could not answer the question a
   * client will actually ask — which is not "did Nova read our data" but "what
   * left, and who could it be used to contact". No schema change was needed for
   * that, which is the only reason it is done this way: 01-event-schema.md is
   * under change control with O-059 open, and a field added to a frozen payload
   * to carry a value two existing fields can carry is a change-control
   * conversation bought for nothing.
   */
  audit_event: {
    export_template_id: string;
    template_version: string;
    scope: { activation_ids: string[]; report_id: string; filters_applied: string[] };
    format: "csv" | "xlsx" | "json";
    row_count: number;
    column_set: string[];
    actor_id: string;
    actor_role: "nova_admin" | "nova_editor" | "client_viewer";
    destination: "download" | "scheduled_email";
  };
};

export type ExportOptions = {
  actorId: string;
  actorRole: "nova_admin" | "nova_editor" | "client_viewer";
  now?: Date;
  format?: "csv" | "xlsx" | "json";
  destination?: "download" | "scheduled_email";
  /**
   * What the file is for. Omitted means participation_report, which is what
   * this function has always produced.
   *
   * Typed as a plain string rather than as ExportPurposeId, because the value
   * arrives from a query string and a type that says it cannot be wrong is a
   * type that stops the runtime check being written. purposeFor refuses an
   * unknown one rather than defaulting.
   */
  purpose?: string;
  /**
   * The columns the caller actually needs. Omitted means everything the purpose
   * carries.
   *
   * Absent rather than empty for "no preference": an empty list is refused,
   * because it is far more likely to be a caller sending the parameter with
   * nothing in it than a genuine request for rows with no columns.
   */
  fields?: readonly string[];
};

/** The columns this export can produce, named once so nothing downstream can disagree. */
export const EXPORT_COLUMNS: readonly string[] = purposeFor("participation_report").columns;

export const EXPORT_TEMPLATE_ID = "xp1.attendees.minimal";
export const EXPORT_TEMPLATE_VERSION = "1";

/**
 * Refuse unless the client behind this activation has a processing agreement.
 *
 * The join is activation → client, and it is the activation's own client_id
 * rather than anything the caller supplied: a request that could name the
 * client whose agreement is checked is a request that could name a different
 * one from the activation it is exporting.
 *
 * EITHER BASIS PERMITS IT. A recorded agreement is the real one; a
 * grandfathered marker is 126's owner decision that clients already on the
 * service when the rule arrived are not broken by it. The two are separate
 * columns precisely so the ones still owing a signed document stay findable,
 * and this function is the one place that treats them alike.
 *
 * An activation whose client row has vanished is refused rather than allowed.
 * There is a foreign key making that unreachable; if it is ever reached, "no
 * client, no agreement, no file" is the answer that fails towards not
 * disclosing.
 */
async function requireProcessingAgreement(c: Queryable, activationId: string): Promise<void> {
  const { rows } = await c.query<{ client_id: string; permitted: boolean }>(
    `select cl.client_id,
            (cl.agreement_signed_on is not null
              or cl.agreement_grandfathered_at is not null) as permitted
       from activation a
       join client cl on cl.client_id = a.client_id
      where a.activation_id = $1`,
    [activationId],
  );

  const row = rows[0];
  if (row === undefined) throw new ExportAgreementMissing(`behind activation ${activationId}`);
  if (!row.permitted) throw new ExportAgreementMissing(row.client_id);
}

export async function exportActivation(
  c: Queryable,
  activationId: string,
  options: ExportOptions,
): Promise<ActivationExport> {
  // Resolved before anything is read, so a bad purpose or a bad field list
  // costs no query and discloses nothing. Re-resolved here rather than taken
  // from the caller for the reason resolveShape's own comment gives.
  const shape = resolveShape(options.purpose, options.fields);
  const spec = shape.purpose;

  const access = await requireAccess(c, activationId);

  // HANDOFF RULE 12, AND IT RUNS BEFORE A SINGLE ROW OF PERSONAL DATA IS READ.
  // Placed here rather than beside the assembly below on the reasoning
  // resolveShape's placement already sets out: a refusal that costs a query
  // over attendee rows has already read the thing it was refusing to hand over,
  // and the difference matters the day a log or an error carries a fragment of
  // what was in memory. Access first, because "you may not read this" outranks
  // "this client has no agreement" — telling a stranger which of Nova's clients
  // lack paperwork would be its own small disclosure.
  await requireProcessingAgreement(c, activationId);

  const now = options.now ?? new Date();

  // The free-text questions, only for the purpose that quotes them, and from
  // the same live field set the survey report counts (survey.ts).
  const freeText = shape.columns.includes("free_text") ? await readFreeTextQuestions(c, activationId) : [];
  // The wheel's spins, only for the purpose that carries them, and the file is
  // then exactly the people who spun (156, decision C10).
  const spins = shape.columns.includes("wheel") ? await readSpins(c, activationId) : null;
  const everyone = await readRows(c, activationId, shape.columns, freeText, spins);
  const read = spins === null ? everyone : everyone.filter((line) => line.row.wheel !== undefined);

  // An Admin's actions (152), before the purpose decides anything. Withheld is
  // absent from every file. Erased stays as its tombstone where the purpose has
  // a row for everybody, and is absent where it requires a consent.
  const notWithheld = read.filter((line) => !line.withheld);
  const keepErased = spec.requires.length === 0;
  const candidates = notWithheld.filter((line) => keepErased || !line.erased).map((line) => line.row);
  const adminActions = {
    withheld_and_absent: read.length - notWithheld.length,
    erased: notWithheld.filter((line) => line.erased).length,
    erased_and_absent: keepErased ? 0 : notWithheld.filter((line) => line.erased).length,
    note:
      "An Admin can withhold a person from every file and can erase a person's details. A withheld person is " +
      "kept on the activation and is not in this file. An erased person is a row with nothing identifying on " +
      "it in a file that has a row for everybody, so the count stays true, and is absent from a file that " +
      "requires a consent. Their attendee reference is how to find them in a file produced earlier, and an " +
      "erasure is the instruction to delete them from it.",
  };
  const withdrawals = await readWithdrawals(c, activationId);
  const wording = await readWording(c, activationId);
  const excluded = await readExcluded(c, activationId);
  const observed = await readObservedPurposes(c, activationId);

  // The structural refusal, in one line, and the reason it is a partition
  // rather than a filter on the query: the omitted people still have to be
  // counted, because a marketing list that is smaller than the participation
  // report of the same activation with no account of the difference is the
  // shrinking file exportRule.ts refuses to produce.
  const rows = candidates.filter((row) => rowSatisfies(spec, row));
  const omittedRows = candidates.filter((row) => !rowSatisfies(spec, row));

  const withheld = {
    withdrawn: rows.filter((r) => r.personal_data === "withdrawn").length,
    never_granted: rows.filter((r) => r.personal_data === "never_granted").length,
  };

  const caveats = [
    "Rows are the state of the spine at the moment this ran. A device that was offline when the activation closed still syncs afterwards, so a later export of the same activation can hold more. Compare spine_high_water to tell.",
    "Counts here are of captures that count. Voided captures are excluded and their number is above.",
  ];
  if (access.kind === "granted") {
    caveats.push(
      "This file was produced by a client viewer, so it contains exactly the activation granted to them and cannot contain anything else.",
    );
  }
  if (spins !== null) {
    caveats.push(
      `This file is the prize wheel's: it has a row for each of the ${read.length} people who spun and are on the record, and none for the ${everyone.length - read.length} registrations that did not spin. A blank is a row like a prize.`,
    );
  }
  if (shape.minimised) {
    caveats.push(
      `This file was narrowed on request: it carries ${shape.columns.length} of the ${spec.columns.length} columns this template can produce. A column that is missing from every row was not asked for. A column that is present and empty was withheld, and the row says which and why.`,
    );
  } else {
    caveats.push(
      `This file carries every column the ${spec.id} template can produce. If you need fewer, ask for fewer. The export takes a field list, and a column that was never handed over is one nobody has to look after.`,
    );
  }

  const artefact: ActivationExport = {
    manifest: {
      activation_id: activationId,
      generated_at: now.toISOString(),
      actor_id: options.actorId,
      actor_role: options.actorRole,
      access,
      access_note: describeAccess(access),
      row_count: rows.length,
      withdrawal_count: withdrawals.length,
      spine_high_water: excluded.high_water,
      excluded_voided: {
        registrations: excluded.voided_registrations,
        all_events: excluded.voided_events,
        note: "A voided capture did not happen the way it was recorded and is not in this file. It is still in the record, and the void is too.",
      },
      withheld,
      admin_actions: adminActions,
      purpose: purposeManifest(shape, omittedRows, observed),
      retention: RETENTION,
      rule: EXPORT_RULE,
      caveats,
      column_definitions: Object.fromEntries(
        shape.columns
          .filter((column) => COLUMN_DEFINITIONS[column] !== undefined)
          .map((column) => [column, COLUMN_DEFINITIONS[column] as string]),
      ),
    },
    rows,
    withdrawals,
    consent_wording: wording,
    audit_event: {
      export_template_id: spec.template_id,
      template_version: EXPORT_TEMPLATE_VERSION,
      scope: {
        activation_ids: [activationId],
        report_id: spec.template_id,
        filters_applied: filtersApplied(shape),
      },
      format: options.format ?? "json",
      row_count: rows.length,
      column_set: [...shape.columns],
      actor_id: options.actorId,
      actor_role: options.actorRole,
      destination: options.destination ?? "download",
    },
  };

  // Last, and on the finished object rather than on the parts. exportPurpose.ts
  // has the argument for why a gate that repeats work the code above already did
  // is worth having; the short version is that every failure this repository has
  // recorded on the report path reported success.
  assertPurposeHonoured(shape, artefact);

  return artefact;
}

// -------------------------------------------------------------- the sentences

/**
 * The purpose's own account of itself, assembled once so the file and the audit
 * record cannot describe the same export differently.
 */
function purposeManifest(
  shape: ResolvedShape,
  omittedRows: readonly ExportRow[],
  observed: ReadonlySet<string>,
): ExportPurposeManifest {
  const spec = shape.purpose;

  const byRequirement = spec.requires.map((purpose) => {
    const failing = omittedRows.filter((row) => (row.consent as Record<string, string>)[purpose] !== "granted");
    return {
      purpose,
      withdrawn: failing.filter((row) => (row.consent as Record<string, string>)[purpose] === "withdrawn").length,
      never: failing.filter((row) => (row.consent as Record<string, string>)[purpose] !== "withdrawn").length,
    };
  });

  const unobserved = spec.requires.filter((purpose) => !observed.has(purpose));

  return {
    id: spec.id,
    template_id: spec.template_id,
    says: spec.says,
    responsibility: spec.responsibility,
    requires: [...spec.requires],
    columns: [...shape.columns],
    columns_available: [...spec.columns],
    minimised: shape.minimised,
    omitted: {
      total: omittedRows.length,
      by_requirement: byRequirement,
      note:
        spec.requires.length === 0
          ? "Nobody is left out of this file for want of a consent. Every capture that counts has a row, and what may be said about each person is on the row."
          : `${omittedRows.length} ${omittedRows.length === 1 ? "person who took part is" : "people who took part are"} not in this file, because this file requires a live consent for ${list(spec.requires)} and ${omittedRows.length === 1 ? "they do" : "they do"} not hold all of them. ` +
            "The figures per requirement overlap: somebody who consented to none of them is counted against each, so they add up to more than the total. " +
            "To reconcile against a headline attendance figure, use the participation report of the same activation: it has a row for everybody, including the people missing from this one.",
    },
    unobserved_requirements: {
      purposes: [...unobserved],
      note:
        unobserved.length === 0
          ? "Every consent this file requires was agreed to or withdrawn by somebody at this activation, so an empty result here would mean people declined rather than that they were never asked."
          : `No consent event of any kind was recorded at this activation for ${list(unobserved)}, which this file requires. That is not the same as everybody declining. ` +
            "There is no event for a refusal, because a refusal is the absence of a consent, so a purpose nobody was asked about and a purpose everybody refused look identical from here, and those are opposite facts. " +
            "Before concluding anything from how few rows this file has, check whether the registration form for this activation offered it at all.",
    },
    rule: purposeRule(shape),
  };
}

/**
 * The purpose's rules, in the file.
 *
 * Separate from EXPORT_RULE rather than appended to it, because the two answer
 * different questions and a reader deciding what they may do with this file
 * needs to know which is which. EXPORT_RULE is about one person: what may be
 * said about them given what they agreed to. These are about the artefact: who
 * is in it and which columns it has.
 */
function purposeRule(shape: ResolvedShape): string[] {
  const spec = shape.purpose;
  const rules = [spec.says, spec.responsibility];

  if (spec.requires.length > 0) {
    rules.push(
      `Inclusion in this file required a live consent for ${list(spec.requires)}. Somebody who declined, withdrew, or was never asked for any one of them is absent rather than blank, so there is nothing in this file that needs filtering out before it is used.`,
    );
  }

  rules.push(
    shape.minimised
      ? `This file carries ${shape.columns.length} of the ${spec.columns.length} columns the template can produce, because that is what was asked for. A column missing from every row was not requested; a column present and empty was withheld by consent and the row says so.`
      : `This file carries all ${spec.columns.length} columns the template can produce.`,
  );

  return rules;
}

/**
 * What goes into the audit record's `filters_applied`.
 *
 * Strings, because that is what the frozen payload shape holds. Written as
 * `key=value` so that a query over `report_access.detail` during an incident can
 * find every marketing list ever taken with a `like`, without anybody having to
 * parse json in a hurry.
 */
function filtersApplied(shape: ResolvedShape): string[] {
  const applied = [`purpose=${shape.purpose.id}`];
  for (const required of shape.purpose.requires) applied.push(`requires_live_consent=${required}`);
  if (shape.minimised) {
    applied.push(`columns=${shape.columns.length}_of_${shape.purpose.columns.length}`);
  }
  return applied;
}

/**
 * The retention sentences, one constant so they cannot drift per purpose.
 *
 * Read the type's own comment for why the periods are null. In short: nothing
 * in XP1 deletes attendee data, RETENTION.md says so in its first paragraph, and
 * a number written here would be answering the question a legal reviewer is
 * currently being asked.
 */
const RETENTION: ExportRetention = Object.freeze({
  server: Object.freeze({
    deletes_attendee_data: false,
    erases_on_request: true,
    period_months: null,
    says:
      "Nothing in XP1 deletes attendee data on a schedule. An Admin can erase one person on request, which deletes their details in place everywhere this service holds them and keeps a record with nobody's details on it so the counts stay true; the service's backups are not edited and age out on their normal schedule. No retention period is set: this is not a period nobody has told you, it is a period that does not exist yet, and the difference matters if you are relying on one.",
  }),
  this_file: Object.freeze({
    period_months: null,
    held_by: "the client, as controller",
    says:
      "This file is now yours and Nova cannot reach it. Once it is opened it is a spreadsheet on a laptop, and no protection applied to the file survives that. How long you keep it, who you copy it to, and deleting it when you no longer need it are yours to decide and yours to be able to evidence. In particular: a withdrawal recorded after this file was produced cannot appear in it, so acting on the withdrawal records in a later export is the only way a person who has asked you to stop is actually stopped.",
  }),
  source: "xp/RETENTION.md",
});

const list = (items: readonly string[]): string =>
  items.length === 0
    ? ""
    : items.length === 1
      ? String(items[0])
      : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;

// ------------------------------------------------------------------- the rows

type RowShape = {
  attendee_ref: string;
  registered_at?: string;
  format?: string | null;
  capture_channel?: string | null;
  identity_assurance?: string | null;
  email_verified_at?: string | null;
  display_name?: string | null;
  contact_email?: string | null;
  deliver_to_email?: string | null;
  phone?: string | null;
  visits?: number | string;
  free_text_raw?: Record<string, unknown> | string | null;
  withheld: boolean | null;
  erased: boolean | null;
  consent: Record<string, string> | string | null;
};

/** One read row, with what an Admin's actions say about it beside the row itself. */
type ReadRow = { row: ExportRow; withheld: boolean; erased: boolean };

/**
 * The one place an export column name becomes SQL.
 *
 * A static map from the frozen column list to a fixed expression. A name that
 * is not a key here contributes no SQL at all, so no string a caller sends can
 * reach the query even if resolveShape's refusal were removed — which is the
 * property worth having, since the alternative shape for this is interpolating
 * a caller's field list into a select list.
 *
 * `consent` and the three explanatory columns are absent because they are not
 * read from a row: the consent map is aggregated separately below and the rest
 * are computed from it by exportRule.ts.
 */
const COLUMN_SQL: Partial<Record<ExportColumn, string>> = Object.freeze({
  registered_at: `${utcIso("l.occurred_at")} as registered_at`,
  format: "l.format",
  // Through attendee_contact (152), so a correction an Admin recorded is what
  // the file says and an erased person's details are nothing at all.
  display_name: "attendee_contact(l.activation_id, l.attendee_ref, l.payload -> 'contact') ->> 'display_name' as display_name",
  contact_email: "attendee_contact(l.activation_id, l.attendee_ref, l.payload -> 'contact') ->> 'contact_email' as contact_email",
  deliver_to_email: "attendee_contact(l.activation_id, l.attendee_ref, l.payload -> 'contact') ->> 'deliver_to_email' as deliver_to_email",
  phone: "attendee_contact(l.activation_id, l.attendee_ref, l.payload -> 'contact') ->> 'phone' as phone",

  // capture_channel comes off the envelope and the other two off the payload,
  // which looks inconsistent and is not. db/018 put the channel on the envelope
  // because it governs device_signature, a column every event type has; the
  // other two are facts about a registration and nothing else has them.
  capture_channel: "l.capture_channel::text as capture_channel",
  identity_assurance: "l.payload ->> 'identity_assurance' as identity_assurance",
  email_verified_at: `${utcIso("(l.payload ->> 'email_verified_at')::timestamptz")} as email_verified_at`,
});

async function readRows(
  c: Queryable,
  activationId: string,
  columns: readonly ExportColumn[],
  freeText: readonly { field_id: string; question: string }[],
  spins: readonly WheelSpinLine[] | null = null,
): Promise<ReadRow[]> {
  const wanted = new Set<string>(columns);
  const spinOf = new Map((spins ?? []).map((spin) => [spin.attendee_ref, spin]));
  // The written answers, selected only when the purpose carries them and only
  // for the free-text questions, by id as a parameter: the rest of the form is
  // never selected, so no later mistake here can put a job title on the wire.
  const wantsFreeText = wanted.has("free_text");

  // Every column the registration CTE can carry, minus the ones this file will
  // not. The bytes are not selected rather than being selected and dropped: see
  // the header.
  const selected = (Object.keys(COLUMN_SQL) as ExportColumn[]).filter((column) => wanted.has(column));
  const registrationFields = selected.map((column) => COLUMN_SQL[column]).join(",\n         ");
  const outerFields = selected.map((column) => `r.${column}`).join(",\n       ");
  const wantsVisits = wanted.has("visits");

  const { rows } = await c.query<RowShape>(
    // distinct on the attendee, taking the latest registration, because a
    // re-registration is a correction the person made themselves and the export
    // should carry what they last told us rather than both attempts.
    `with registration as (
       select distinct on (l.attendee_ref)
         l.attendee_ref,
         l.spine_seq${registrationFields === "" ? "" : `,
         ${registrationFields}`}${
           wantsFreeText
             ? `,
         (select jsonb_object_agg(e.key, e.value)
            from jsonb_each(attendee_fields(l.activation_id, l.attendee_ref, l.payload -> 'fields')) e
           where e.key = any($2::text[])) as free_text_raw`
             : ""
         }
       from spine_live l
       where l.activation_id = $1
         and l.event_type = 'registration_completed'
         and l.attendee_ref is not null
       order by l.attendee_ref, l.spine_seq desc
     ),${
       wantsVisits
         ? `
     visit as (
       select l.attendee_ref, count(*) as n
       from spine_live l
       where l.activation_id = $1
         and l.event_type = 'checkin_recorded'
         and l.attendee_ref is not null
       group by l.attendee_ref
     ),`
         : ""
     }
     -- The latest word per attendee per purpose, ordered by when the person
     -- acted rather than by when we heard about it. A withdrawal taken offline
     -- at 11:00 and synced at 17:00 still comes after a consent taken at 10:00.
     --
     -- Never narrowed to the purposes the file happens to gate on. The consent
     -- map is the row's account of itself and a purpose missing from it reads as
     -- "never asked", so selecting fewer purposes would make a minimised file
     -- say a person had consented to less than they had.
     consent as (
       select distinct on (l.attendee_ref, l.payload ->> 'purpose')
         l.attendee_ref,
         l.payload ->> 'purpose' as purpose,
         l.event_type            as latest
       from spine_live l
       where l.activation_id = $1
         and l.event_type in ('consent_captured', 'consent_withdrawn')
         and l.attendee_ref is not null
       order by l.attendee_ref, l.payload ->> 'purpose', l.occurred_at desc, l.spine_seq desc
     )
     select
       r.attendee_ref,
       coalesce(s.withheld, false) as withheld,
       coalesce(s.erased, false)   as erased,${wantsFreeText ? `
       r.free_text_raw,` : ""}${outerFields === "" ? "" : `
       ${outerFields},`}${
         wantsVisits
           ? `
       coalesce(v.n, 0) as visits,`
           : ""
       }
       coalesce(
         jsonb_object_agg(c.purpose, c.latest) filter (where c.purpose is not null),
         '{}'::jsonb
       ) as consent
     from registration r${
       wantsVisits
         ? `
     left join visit v   on v.attendee_ref = r.attendee_ref`
         : ""
     }
     left join consent c on c.attendee_ref = r.attendee_ref
     left join attendee_standing s on s.activation_id = $1 and s.attendee_ref = r.attendee_ref
     group by
       r.attendee_ref, r.spine_seq, s.withheld, s.erased${wantsFreeText ? ", r.free_text_raw" : ""}${selected.length === 0 ? "" : `, ${selected.map((column) => `r.${column}`).join(", ")}`}${wantsVisits ? ", v.n" : ""}
     order by r.spine_seq`,
    wantsFreeText ? [activationId, freeText.map((question) => question.field_id)] : [activationId],
  );

  return rows.map((row) => {
    const states = consentStates(row.consent);
    const disclosure = discloseToClient(
      {
        display_name: row.display_name ?? null,
        contact_email: row.contact_email ?? null,
        deliver_to_email: row.deliver_to_email ?? null,
        phone: row.phone ?? null,
      },
      states,
    );

    // The rule first, over all four contact slots, then the projection down to
    // what this file carries. That order is what keeps `withheld_fields`
    // meaning one thing: the rule decides withholding from the consent states
    // alone, and the projection removes columns nobody asked for. Reversing it
    // would have the rule reasoning about absent values and reporting a column
    // as withheld when it was simply not requested.
    const erased = row.erased === true;
    const answers = typeof row.free_text_raw === "string" ? JSON.parse(row.free_text_raw) : row.free_text_raw;
    const full: Record<string, unknown> = {
      attendee_ref: row.attendee_ref,
      registered_at: row.registered_at,
      format: row.format ?? null,
      capture_channel: row.capture_channel ?? null,
      identity_assurance: row.identity_assurance ?? null,
      // Null rather than an empty string, because a spreadsheet renders an empty
      // string and a null identically and a JSON reader does not. The definition
      // in the manifest says empty means never proved.
      email_verified_at: row.email_verified_at ?? null,
      ...disclosure.fields,
      visits: row.visits === undefined ? undefined : toSafeInteger(row.visits, "visits"),
      free_text: freeTextFrom(freeText, answers),
      wheel: wheelFrom(spinOf.get(row.attendee_ref)),
      consent: states,
      // An erased person is a tombstone whatever they consented to (152): the
      // contact slots already came back empty, and the row says why.
      personal_data: erased ? "erased" : disclosure.reason,
      withheld_fields: (erased ? ERASED_FIELDS : disclosure.withheld).filter((field) => wanted.has(field)),
      personal_data_note: erased ? ERASED_NOTE : disclosure.explanation,
    };

    const projected: Record<string, unknown> = {};
    for (const column of columns) projected[column] = full[column];
    return { row: projected as ExportRow, withheld: row.withheld === true, erased };
  });
}

/**
 * Turn the per-purpose latest event type into consent state.
 *
 * A purpose with no entry is absent rather than "never", so that the rule's own
 * default is the one that decides. Two defaults for the same question is how
 * one of them ends up being the permissive one.
 *
 * EXPORTED SINCE attendees.ts, WHICH IS THE SAME QUESTION ASKED BY A DIFFERENT
 * READER. That surface reads the same events for the same people and hands a
 * console the same map, and the alternative was a second copy of these eight
 * lines. Two implementations of "what does the latest event mean" is exactly how
 * one of them acquires a third value meaning no, and the sentence above is the
 * one that must not be restated anywhere: an absent purpose is unknown, and the
 * permissive reading of it is the one a copy would drift towards.
 */
const ERASED_FIELDS = ["display_name", "contact_email", "deliver_to_email", "phone", "free_text"];
const ERASED_NOTE =
  "An Admin erased this person's details, so nothing identifying them is in this file. The row is kept so the " +
  "count stays true. If an earlier file holds this attendee reference, the erasure is the instruction to delete " +
  "them from it.";

/** A person's spin, in the file's words, or undefined for somebody who did not spin. */
function wheelFrom(spin: WheelSpinLine | undefined): ExportWheelSpin | undefined {
  if (spin === undefined) return undefined;
  return {
    spun_at: spin.spun_at,
    lane: spin.lane,
    landed_on: spin.landed_on,
    prize: spin.prize,
    claim_code: spin.claim_code,
    claim_expires_at: spin.claim_expires_at,
    collected_at: spin.collected_at,
  };
}

/** A person's written answers, in the form's order, blank ones left out. */
function freeTextFrom(
  questions: readonly { field_id: string; question: string }[],
  raw: unknown,
): ExportFreeText[] {
  const given = raw !== null && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const out: ExportFreeText[] = [];
  for (const question of questions) {
    const answer = given[question.field_id];
    if (typeof answer === "string" && answer.trim() !== "") {
      out.push({ field_id: question.field_id, question: question.question, answer });
    }
  }
  return out;
}

export function consentStates(raw: RowShape["consent"]): ConsentStates {
  const parsed: unknown = typeof raw === "string" ? JSON.parse(raw) : raw;
  if (parsed === null || typeof parsed !== "object") return {};

  const states: Partial<Record<ConsentPurpose, ConsentState>> = {};
  for (const [purpose, latest] of Object.entries(parsed as Record<string, unknown>)) {
    if (latest === "consent_captured") states[purpose as ConsentPurpose] = "granted";
    else if (latest === "consent_withdrawn") states[purpose as ConsentPurpose] = "withdrawn";
  }
  return states;
}

// ------------------------------------------------------------ the withdrawals

async function readWithdrawals(c: Queryable, activationId: string): Promise<ExportWithdrawal[]> {
  const { rows } = await c.query<ExportWithdrawal>(
    `select
       l.attendee_ref,
       l.payload ->> 'consent_version_id' as consent_version_id,
       l.payload ->> 'purpose'            as purpose,
       l.payload ->> 'method'             as method,
       ${utcIso("l.occurred_at")}         as withdrawn_at
     from spine_live l
     where l.activation_id = $1
       and l.event_type = 'consent_withdrawn'
     order by l.spine_seq`,
    [activationId],
  );
  return rows;
}

// --------------------------------------------------------------- the evidence

async function readWording(c: Queryable, activationId: string): Promise<ExportWording[]> {
  // A consent event cites a hash rather than carrying the paragraph, so the
  // consent is evidence only while the words behind it can still be produced.
  // Carrying them once per file rather than once per row is the whole reason
  // the schema stores them centrally.
  const { rows } = await c.query<ExportWording>(
    `select w.wording_hash, w.consent_version_id, w.purpose, w.locale, w.body
     from consent_wording w
     where w.wording_hash in (
       select l.payload ->> 'wording_hash'
       from spine_live l
       where l.activation_id = $1
         and l.event_type = 'consent_captured'
         and l.payload ? 'wording_hash'
     )
     order by w.consent_version_id, w.purpose, w.locale`,
    [activationId],
  );
  return rows;
}

/**
 * Which consent purposes this activation has any event for at all.
 *
 * Its own query rather than derived from the rows above, and the difference is
 * real: the rows are keyed on a completed registration, and a consent event can
 * exist for an attendee whose registration was voided or has not arrived. Using
 * the rows would report a purpose as never-offered on the strength of which
 * registrations happen to be in the file, which is the exact wrong direction —
 * it would tell a client nobody was asked when somebody was.
 */
async function readObservedPurposes(c: Queryable, activationId: string): Promise<Set<string>> {
  const { rows } = await c.query<{ purpose: string | null }>(
    `select distinct l.payload ->> 'purpose' as purpose
     from spine_live l
     where l.activation_id = $1
       and l.event_type in ('consent_captured', 'consent_withdrawn')`,
    [activationId],
  );
  return new Set(rows.map((row) => row.purpose).filter((purpose): purpose is string => purpose !== null));
}

// -------------------------------------------------------------- the exclusions

async function readExcluded(
  c: Queryable,
  activationId: string,
): Promise<{ voided_registrations: number; voided_events: number; high_water: number }> {
  const { rows } = await c.query<Record<string, number | string>>(
    `select
       count(*) filter (where not a.counts and a.event_type = 'registration_completed') as voided_registrations,
       count(*) filter (where not a.counts)                                             as voided_events,
       coalesce(max(a.spine_seq), 0)                                                    as high_water
     from spine_arrival a
     where a.activation_id = $1`,
    [activationId],
  );

  const row = rows[0] ?? {};
  return {
    voided_registrations: toSafeInteger(row.voided_registrations ?? 0, "voided_registrations"),
    voided_events: toSafeInteger(row.voided_events ?? 0, "voided_events"),
    high_water: toSafeInteger(row.high_water ?? 0, "high_water"),
  };
}